import { MenuAlign, MenuJustify } from "../atoms/menu-content";
import { BoundsResult, checkBounds, waitForNextAnimationFrame } from "./util";

export interface TooltipCorrectionElement {
	align: MenuAlign;
	justify: MenuJustify;
	$menu: HTMLElement | undefined;
	getContainer: () => HTMLElement | Window;
	open: boolean;
}

export interface TooltipCorrectionPriority {
	justify: MenuJustify;
	align: MenuAlign;
}

export const TOOLTIP_DATA_READY_ATTRIBUTE = "data-ready";
const DEFAULT_MAX_BOUNDS_PADDING = 0; // 32;

export const TOOLTIP_CORRECTION_PRIORITY_LIST: TooltipCorrectionPriority[] = [
	{
		justify: "right",
		align: "bottom"
	},
	{
		justify: "left",
		align: "bottom"
	},
	{
		justify: "right",
		align: "top"
	},
	{
		justify: "left",
		align: "top"
	},
	{
		justify: "center",
		align: "bottom"
	},
	{
		justify: "center",
		align: "top"
	}
];

/**
 * Sets the best position for the menu based on the priority list
 * @param $host
 * @param priority
 */
export async function setBestPositionForMenu(
	$host: HTMLElement & TooltipCorrectionElement,
	priority: TooltipCorrectionPriority[] = TOOLTIP_CORRECTION_PRIORITY_LIST
) {
	if (!$host.open || $host.$menu == null) return;
	$host.removeAttribute(TOOLTIP_DATA_READY_ATTRIBUTE);

	// Find the best position for the menu
	const measurements: {
		align: MenuAlign;
		justify: MenuJustify;
		bounds: BoundsResult;
		score: number;
	}[] = [];

	setMenuContentMaxCSSProperties($host, null);

	for (const { justify, align } of priority) {
		$host.justify = justify;
		$host.align = align;
		await waitForNextAnimationFrame();
		if (!$host.open || $host.$menu == null) return;
		const bounds = checkBounds($host.$menu, {
			justify,
			align,
			padding: DEFAULT_MAX_BOUNDS_PADDING,
			$container: $host.getContainer()
		});
		if (bounds.isWithinBounds) {
			setMenuContentMaxCSSProperties($host, bounds);
			setTooltipDataReady($host);
			return;
		}

		measurements.push({
			align,
			justify,
			bounds,
			score: bounds.availableSize.width + bounds.availableSize.height
		});
	}

	const bestMeasurement = measurements.sort((a, b) => b.score - a.score)[0]!;
	if (bestMeasurement != null) {
		$host.justify = bestMeasurement.justify;
		$host.align = bestMeasurement.align;
		setMenuContentMaxCSSProperties($host, bestMeasurement.bounds);
	}

	requestAnimationFrame(() => {
		setTooltipDataReady($host);
	});
}

/**
 * Sets the tooltip data ready attribute
 * @param $host
 */
export function setTooltipDataReady($host: HTMLElement & TooltipCorrectionElement) {
	$host.setAttribute(TOOLTIP_DATA_READY_ATTRIBUTE, "");
}

/**
 * Sets the max height and width of the menu content.
 * @param $host
 * @param bounds
 */
function setMenuContentMaxCSSProperties($host: HTMLElement & TooltipCorrectionElement, bounds: BoundsResult | null) {
	if (bounds != null) {
		$host.style.setProperty("--menu-max-height-adjust", `${bounds.availableSize.height}px`);
		$host.style.setProperty("--menu-max-width-adjust", `${bounds.availableSize.width}px`);
	} else {
		$host.style.removeProperty("--menu-max-height-adjust");
		$host.style.removeProperty("--menu-max-width-adjust");
	}
}
